import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./IncomingInvoiceProcessingPage.scss";
import heroBanner from "../../assets/images/banner-invoice-processing.png";
import Copyright from "../../components/Copyright/Copyright";

const IncomingInvoiceProcessingPage = () => {
    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="feature-page">
            {/* Hero Section */}
            <section className="feature-hero">
                <div className="container">
                    <div className="feature-hero-image">
                        <img src={heroBanner} alt="Incoming Invoice Processing" />
                    </div>
                </div>
            </section>

            {/* Incoming Invoice Processing Information */}
            <section className="feature-info">
                <div className="container">
                    <div className="feature-grid">
                        <div className="feature-details">
                            <h2>Incoming invoice processing</h2>
                            <p>
                                CashManager streamlines your incoming invoice processing with automated workflows,
                                intelligent data extraction, and seamless integration with your existing business systems.
                            </p>

                            <div className="feature-methods three-columns">
                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Automated data extraction</h3>
                                        <p>Advanced OCR and AI-powered technology to automatically extract invoice data with high accuracy.</p>
                                    </div>
                                </div>

                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Smart validation</h3>
                                        <p>Intelligent validation rules and duplicate detection to ensure data integrity and prevent errors.</p>
                                    </div>
                                </div>

                                <div className="feature-method">
                                    <div className="method-content">
                                        <h3>Workflow automation</h3>
                                        <p>Automated approval workflows and routing to streamline the invoice processing cycle.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="feature-features">
                            <h2>Key features</h2>

                            <div className="features-list">
                                <div className="feature-item">
                                    <h3>Multi-format support</h3>
                                    <p>Process invoices in virtually any format including PDF, scanned images, and electronic documents.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>2027 Latvian regulation compliance</h3>
                                    <p>Get ready for 2027 mandatory electronic invoicing compliance with CashManager.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Automatic supplier detection</h3>
                                    <p>Intelligent supplier detection for precise accounting.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Data validation</h3>
                                    <p>Configurable data validation rules to ensure the accuracy and completeness of the invoice data.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Payment scheduling</h3>
                                    <p>Automated payment scheduling based on due dates and cash flow optimization strategies.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Integration capabilities</h3>
                                    <p>Seamless integration with accounting systems, ERP platforms, and payment processing solutions.</p>
                                </div>

                                <div className="feature-item">
                                    <h3>Audit trail</h3>
                                    <p>Complete audit trail of all invoice processing activities for compliance and record-keeping.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Supported Formats Section */}
            <section className="supported-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Supported formats & sources</h2>
                        <p>Thanks to our AI technology, CashManager supports invoices in virtually any format and language to handle your diverse invoice processing needs. <Link to="/contact">Contact us</Link> to discuss your specific requirements.</p>
                    </div>

                    <div className="items-grid">
                        <div className="item">
                            <h3>File formats</h3>
                            <ul>
                                <li>PDF documents</li>
                                <li>Scanned images (JPG, PNG, TIFF)</li>
                                <li>Electronic invoices (XML, EDI)</li>
                                <li>Excel spreadsheets</li>
                                <li>Word documents</li>
                                <li>Virtually any other format - <Link to="/contact">Contact us</Link> for support</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>Input sources</h3>
                            <ul>
                                <li>Email attachments</li>
                                <li>Latvija.lv system</li>
                                {/* <li>File uploads</li> */}
                                <li>API integrations</li>
                                <li>FTP/SFTP transfers</li>
                                <li>Cloud storage</li>
                                <li>Other sources - <Link to="/contact">Contact us</Link> for options</li>
                            </ul>
                        </div>

                        <div className="item">
                            <h3>Languages supported</h3>
                            <ul>
                                <li>Latvian</li>
                                <li>English</li>
                                <li>Lithuanian</li>
                                <li>Estonian</li>
                                <li>Traditional Chinese</li>
                                <li>Virtually any language - <Link to="/contact">Contact us</Link> for more details</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Processing Workflow */}
            <section className="implementation-process">
                <div className="container">
                    <div className="section-header">
                        <h2>Processing workflow</h2>
                        <p>Streamlined invoice processing in just a few simple steps</p>
                    </div>

                    <div className="process-steps">
                        <div className="process-step">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Invoice capture</h3>
                                <p>Automatically capture invoices from various sources including email, uploads, and API integrations.</p>
                            </div>
                        </div>

                        <div className="process-step">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Data extraction</h3>
                                <p>Extract key information using OCR and AI technology with automatic validation and error checking.</p>
                            </div>
                        </div>

                        <div className="process-step">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Data validation</h3>
                                <p>Validate the invoice data using configurable validation rules.</p>
                            </div>
                        </div>

                        <div className="process-step">
                            <div className="step-number">4</div>
                            <div className="step-content">
                                <h3>Payment processing</h3>
                                <p>Schedule payments and integrate with your banking systems for automated payment execution.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to automate your invoice processing?</h2>
                        <p>Contact our team to discuss your incoming invoice processing requirements and streamline your financial operations.</p>
                        <Link to="/onboarding" className="btn btn-secondary btn-large">Get started</Link>
                    </div>
                </div>
            </section>

            {/* Copyright Section */}
            <Copyright />
        </div>
    );
};

export default IncomingInvoiceProcessingPage;
